import { Injectable, inject } from '@angular/core';
import {
  CreditActionPayload,
  CreditDashboardFiltre,
  CreditSaveBonCommande,
  CreditSaveComite,
  CreditSaveDecisionFinale,
  CreditSaveDemande,
  CreditSaveFacture,
  CreditSaveMagasin,
  CreditSaveTirage,
  CreditUpdateDemande,
} from '../../interfaces/credit.interface';
import { CreditAnalyseService } from './credit-analyse.service';
import { CreditDashboardService } from './credit-dashboard.service';
import { CreditDemandeService } from './credit-demande.service';
import { CreditEmployeurService } from './credit-employeur.service';
import { CreditGarantieService } from './credit-garantie.service';
import { CreditWorkflowService } from './credit-workflow.service';

/**
 * Façade CreditService — délègue vers les sous-services spécialisés.
 * Tous les composants injectent toujours uniquement ce service.
 *
 * Sous-services :
 *  - CreditDashboardService  : stats, dashboard siège/agence
 *  - CreditAnalyseService    : analyse financière complète
 *  - CreditGarantieService   : garanties, cautions, uploads
 *  - CreditWorkflowService   : workflow, comité, décision, tirage
 *  - CreditDemandeService    : demandes, clients, référentiels, signataires
 *  - CreditEmployeurService  : employeurs
 */
@Injectable({ providedIn: 'root' })
export class CreditService {
  private readonly dashboard = inject(CreditDashboardService);
  private readonly analyse = inject(CreditAnalyseService);
  private readonly garantie = inject(CreditGarantieService);
  private readonly workflow = inject(CreditWorkflowService);
  private readonly demande = inject(CreditDemandeService);
  private readonly employeur = inject(CreditEmployeurService);

  /**
   * @deprecated Utiliser DocumentUrlHelper avec ModuleDocument.CREDIT à la place.
   * Conservé temporairement pour compatibilité avec l'ancien système.
   */
  readonly prefixDocumentCredit = 'https://crm-fichiers.creditaccess.ci/crm/credit-ca/';

  /**
   * @deprecated Utiliser DocumentUrlHelper avec ModuleDocument.EMPLOYEUR à la place.
   * Conservé temporairement pour compatibilité avec l'ancien système.
   */
  readonly prefixDocumentEmployeur = 'https://crm-fichiers.creditaccess.ci/crm/employeur/';


  // ═══════════════════════════════════════════════════════════════════════════
  // DASHBOARD
  // ═══════════════════════════════════════════════════════════════════════════
  getDashboard() {
    return this.dashboard.getDashboard();
  }
  filtrerDashboard(f: CreditDashboardFiltre) {
    return this.dashboard.filtrerDashboard(f);
  }
  getDashboardTypeCredit() {
    return this.dashboard.getDashboardTypeCredit();
  }
  getDashboardStatut() {
    return this.dashboard.getDashboardStatut();
  }
  getStatsByAgence() {
    return this.dashboard.getStatsByAgence();
  }
  getStatsByRegion() {
    return this.dashboard.getStatsByRegion();
  }
  getZonesByRegion(id: number) {
    return this.dashboard.getZonesByRegion(id);
  }
  getSousZonesByZone(id: number) {
    return this.dashboard.getSousZonesByZone(id);
  }
  getTbByProd() {
    return this.dashboard.getTbByProd();
  }
  getTbByProdFiltre(a: string, d: string, f: string) {
    return this.dashboard.getTbByProdFiltre(a, d, f);
  }
  getDetailAgence(code: string, page = 1, perPage = 20) {
    return this.dashboard.getDetailAgence(code, page, perPage);
  }
  filtrerDetailAgence(code: string, f: CreditDashboardFiltre, page = 1, perPage = 20) {
    return this.dashboard.filtrerDetailAgence(code, f, page, perPage);
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // DEMANDES — Référentiels, clients, fiche, documents
  // ═══════════════════════════════════════════════════════════════════════════
  getTypesCredit() {
    return this.demande.getTypesCredit();
  }
  getTypesActivite() {
    return this.demande.getTypesActivite();
  }
  getPaysCommuneData() {
    return this.demande.getPaysCommuneData();
  }
  getZones() {
    return this.demande.getZones();
  }
  getARsByZone(zoneId: number) {
    return this.demande.getARsByZone(zoneId);
  }
  getSuperviseursPmeEtChefAgence() {
    return this.demande.getSuperviseursPmeEtChefAgence();
  }
  searchClientCredit(code: string) {
    return this.demande.searchClientCredit(code);
  }
  updateClientPP(data: Record<string, unknown>) {
    return this.demande.updateClientPP(data);
  }
  updateClientPM(data: Record<string, unknown>) {
    return this.demande.updateClientPM(data);
  }
  getListeDemandes(action?: 'cloture' | 'rejette') {
    return this.demande.getListeDemandes(action);
  }
  getFicheCredit(ref: string) {
    return this.demande.getFicheCredit(ref);
  }
  getDetailsDemande(ref: string) {
    return this.demande.getDetailsDemande(ref);
  }
  getResumeAnalyse(ref: string) {
    return this.demande.getResumeAnalyse(ref);
  }
  getAnalyseFinanciere(ref: string) {
    return this.demande.getAnalyseFinanciere(ref);
  }
  getObservations(ref: string) {
    return this.demande.getObservations(ref);
  }
  getDocuments(ref: string) {
    return this.demande.getDocuments(ref);
  }
  uploadDocument(formData: FormData) {
    return this.demande.uploadDocument(formData);
  }
  deleteDocument(id: number | string) {
    return this.demande.deleteDocument(id);
  }
  saveDemandeCredit(data: CreditSaveDemande) {
    return this.demande.saveDemandeCredit(data);
  }
  updateDemandeCredit(data: CreditUpdateDemande) {
    return this.demande.updateDemandeCredit(data);
  }
  savePreEvaluation(data: Record<string, unknown>) {
    return this.demande.savePreEvaluation(data);
  }
  saveMagasin(data: CreditSaveMagasin) {
    return this.demande.saveMagasin(data);
  }
  saveFacture(data: CreditSaveFacture) {
    return this.demande.saveFacture(data);
  }
  saveBonCommande(data: CreditSaveBonCommande) {
    return this.demande.saveBonCommande(data);
  }
  saveInfoCrAuto(data: Record<string, unknown>) {
    return this.demande.saveInfoCrAuto(data);
  }
  deleteInfoCrAuto(id: number) {
    return this.demande.deleteInfoCrAuto(id);
  }
  recupererSignataire(codeClient: string) {
    return this.demande.recupererSignataire(codeClient);
  }
  updateSignataire(data: Record<string, unknown>) {
    return this.demande.updateSignataire(data);
  }
  updateSignataireAvecPhoto(fd: FormData) {
    return this.demande.updateSignataireAvecPhoto(fd);
  }
  getActivitesDemande(ref: string) {
    return this.demande.getActivitesDemande(ref);
  }
  getLogs(ref: string, type: 'documents' | 'modifications' | 'ajouts') {
    return this.demande.getLogs(ref, type);
  }
  getHistoriqueTirages(ref: string) {
    return this.demande.getHistoriqueTirages(ref);
  }
  getHistoriqueDemande(codeClient?: string|null, params?: { debourse?: 'oui' | 'non' }) {
    return this.demande.getHistoriqueDemande(codeClient, params);
  }
  getDocumentsObligatoireAndActions(ref: string) {
    return this.demande.getDocumentsObligatoireAndActions(ref);
  }
  // ═══════════════════════════════════════════════════════════════════════════
  // ANALYSE FINANCIÈRE
  // ═══════════════════════════════════════════════════════════════════════════
  saveActivite(data: Record<string, unknown>) {
    return this.analyse.saveActivite(data);
  }
  deleteActivite(id: number) {
    return this.analyse.deleteActivite(id);
  }
  saveVenteMensuelle(data: Record<string, unknown>) {
    return this.analyse.saveVenteMensuelle(data);
  }
  deleteVenteMensuelle(id: number) {
    return this.analyse.deleteVenteMensuelle(id);
  }
  saveVenteJournaliere(data: Record<string, unknown>) {
    return this.analyse.saveVenteJournaliere(data);
  }
  deleteVenteJournaliere(id: number) {
    return this.analyse.deleteVenteJournaliere(id);
  }
  saveAchatMensuel(data: Record<string, unknown>) {
    return this.analyse.saveAchatMensuel(data);
  }
  deleteAchatMensuel(id: number) {
    return this.analyse.deleteAchatMensuel(id);
  }
  saveMargeCommerciale(data: Record<string, unknown>) {
    return this.analyse.saveMargeCommerciale(data);
  }
  updateMargeCommerciale(id: number, data: Record<string, unknown>) {
    return this.analyse.updateMargeCommerciale(id, data);
  }
  deleteMargeCommerciale(id: number) {
    return this.analyse.deleteMargeCommerciale(id);
  }
  saveActifCirculantStock(data: Record<string, unknown>) {
    return this.analyse.saveActifCirculantStock(data);
  }
  deleteActifCirculantStock(id: number) {
    return this.analyse.deleteActifCirculantStock(id);
  }
  saveStock(data: Record<string, unknown>) {
    return this.analyse.saveStock(data);
  }
  deleteStock(id: number) {
    return this.analyse.deleteStock(id);
  }
  getListeTypeCharge() {
    return this.analyse.getListeTypeCharge();
  }
  saveChargeExploitation(data: Record<string, unknown>) {
    return this.analyse.saveChargeExploitation(data);
  }
  saveImprevuChargeExploitation(data: {
    activite?: number | null;
    imprevu?: number | null;
    refDemande?: string;
    charge?: number | null;
    montant?: number | null;
    commentaire?: string;
    chargeExploitation?: number | null;
  }) {
    return this.analyse.saveImprevuChargeExploitation(data);
  }
  saveImprevuChargeFamilial(data: { imprevu?: number | null; refDemande?: string }) {
    return this.analyse.saveImprevuChargeFamilial(data);
  }
  deleteChargeExploitation(id: number) {
    return this.analyse.deleteChargeExploitation(id);
  }
  saveTresorerie(data: Record<string, unknown>) {
    return this.analyse.saveTresorerie(data);
  }
  deleteTresorerie(id: number) {
    return this.analyse.deleteTresorerie(id);
  }
  saveCreanceClient(data: Record<string, unknown>) {
    return this.analyse.saveCreanceClient(data);
  }
  updateCreanceClient(id: number, data: Record<string, unknown>) {
    return this.analyse.updateCreanceClient(id, data);
  }
  deleteCreanceClient(id: number) {
    return this.analyse.deleteCreanceClient(id);
  }
  saveAvanceFournisseur(data: Record<string, unknown>) {
    return this.analyse.saveAvanceFournisseur(data);
  }
  updateAvanceFournisseur(id: number, data: Record<string, unknown>) {
    return this.analyse.updateAvanceFournisseur(id, data);
  }
  deleteAvanceFournisseur(id: number) {
    return this.analyse.deleteAvanceFournisseur(id);
  }
  saveDetteFournisseur(data: Record<string, unknown>) {
    return this.analyse.saveDetteFournisseur(data);
  }
  updateDetteFournisseur(id: number, data: Record<string, unknown>) {
    return this.analyse.updateDetteFournisseur(id, data);
  }
  deleteDetteFournisseur(id: number) {
    return this.analyse.deleteDetteFournisseur(id);
  }
  saveDetteEntreprise(data: Record<string, unknown>) {
    return this.analyse.saveDetteEntreprise(data);
  }
  updateDetteEntreprise(id: number, data: Record<string, unknown>) {
    return this.analyse.updateDetteEntreprise(id, data);
  }
  deleteDetteEntreprise(id: number) {
    return this.analyse.deleteDetteEntreprise(id);
  }
  saveProfilEntrepreneur(data: Record<string, unknown>) {
    return this.analyse.saveProfilEntrepreneur(data);
  }
  saveTresorerieFamille(data: Record<string, unknown>) {
    return this.analyse.saveTresorerieFamille(data);
  }
  deleteTresorerieFamille(id: number) {
    return this.analyse.deleteTresorerieFamille(id);
  }
  saveChargeFamille(data: Record<string, unknown>) {
    return this.analyse.saveChargeFamille(data);
  }
  deleteChargeFamille(id: number | string) {
    return this.analyse.deleteChargeFamille(id);
  }
  saveProfilFamilial(data: Record<string, unknown>) {
    return this.analyse.saveProfilFamilial(data);
  }
  saveMembreMenage(data: Record<string, unknown>) {
    return this.analyse.saveMembreMenage(data);
  }
  deleteMembreMenage(id: number) {
    return this.analyse.deleteMembreMenage(id);
  }
  saveCreditAnterieur(data: Record<string, unknown>) {
    return this.analyse.saveCreditAnterieur(data);
  }
  saveCreditAnterieurActivite(data: Record<string, unknown>) {
    return this.analyse.saveCreditAnterieurActivite(data);
  }
  /** Évaluation prêt one shot — partenaire (type 0 = Client, 1 = Fournisseur ; `evaluationPretOneShot` = id pour modifier). */
  saveEvaluationOneShot(data: {
    refDemande: string;
    type: 0 | 1;
    nom: string;
    ca: number;
    evaluationPretOneShot?: string | number;
  }) {
    return this.analyse.saveEvaluationOneShot(data);
  }
  /** Liste des partenaires (évaluations one shot) enregistrés pour une demande. */
  getEvaluationOneShotListe(refDemande: string) {
    return this.analyse.getEvaluationOneShotListe(refDemande);
  }
  /** Supprime une ligne « evaluationPretOneShot » via sa référence. */
  deleteEvaluationOneShot(id: string | number) {
    return this.analyse.deleteEvaluationOneShot(id);
  }
  /** Flux/trésorerie one shot — enregistre/modifie (`evaluationPretOneShot` = id pour modifier). */
  saveEvaluationOneShotFlux(data: {
    refDemande: string;
    activite: number;
    type: number;
    institution: number;
    montant: number;
    sens: number;
    mois: string;
    evaluationPretOneShot?: number;
  }) {
    return this.analyse.saveEvaluationOneShotFlux(data);
  }
  /** Flux/trésorerie one shot — liste pour une demande. */
  getFluxTresoList(refDemande: string) {
    return this.analyse.getFluxTresoList(refDemande);
  }
  /** Flux/trésorerie one shot — suppression via id. */
  deleteEvalOneShotFluxTreso(id: string | number) {
    return this.analyse.deleteEvalOneShotFluxTreso(id);
  }
  /** Transaction à préfinancer — enregistre/modifie (`evaluationPretOneShot` = id pour modifier). */
  saveTransactionPrefinancer(data: {
    refDemande: string;
    activite: number;
    montantMarche: number;
    avanceRecue: number;
    soldeArecevoir: number;
    dateReglementPrevue: string;
    modaliteReglement: number;
    factureNormalisee: number;
    numeroFacture: string;
    dateEmission: string;
    referenceAttestation: string;
    description: string;
    compteCa: number;
    compteAutreInstitution: number;
    observations: string;
    evaluationPretOneShot?: number;
  }) {
    return this.analyse.saveTransactionPrefinancer(data);
  }
  /** Transaction à préfinancer — infos enregistrées pour une demande. */
  getInfosTransactionPrefinancer(refDemande: string) {
    return this.analyse.getInfosTransactionPrefinancer(refDemande);
  }
  /** Fiche contre-évaluation — enregistre/modifie une ligne (`contreEvaluation` = id pour modifier). */
  saveCrContreEvaluation(data: {
    refDemande: string;
    activite: number;
    typeDonnee: string;
    infoCollecte: number | string;
    commentaire: string;
    contreEvaluation?: number;
  }) {
    return this.analyse.saveCrContreEvaluation(data);
  }
  /** Fiche contre-évaluation — liste des lignes enregistrées pour une demande. */
  getContrEvalSup(refDemande: string) {
    return this.analyse.getContrEvalSup(refDemande);
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // GARANTIES
  // ═══════════════════════════════════════════════════════════════════════════
  getGarantiesDemande(ref: string) {
    return this.garantie.getGarantiesDemande(ref);
  }
  saveActifGarantie(data: Record<string, unknown>) {
    return this.garantie.saveActifGarantie(data);
  }
  deleteActifGarantie(id: number) {
    return this.garantie.deleteActifGarantie(id);
  }
  saveCautionSolidaire(data: Record<string, unknown>) {
    return this.garantie.saveCautionSolidaire(data);
  }
  deleteCautionSolidaire(id: number | string) {
    return this.garantie.deleteCautionSolidaire(id);
  }
  updatePhotoProfilCaution(cautionId: number, file: File) {
    return this.garantie.updatePhotoProfilCaution(cautionId, file);
  }
  saveImageCaution(cautionId: number, libelle: string, file: File) {
    return this.garantie.saveImageCaution(cautionId, libelle, file);
  }
  saveDocumentCaution(cautionId: number, libelle: string, file: File) {
    return this.garantie.saveDocumentCaution(cautionId, libelle, file);
  }
  deleteImageCaution(imageId: number | string) {
    return this.garantie.deleteImageCaution(imageId);
  }
  deleteDocumentCaution(docId: number | string) {
    return this.garantie.deleteDocumentCaution(docId);
  }
  uploadPhotoCaution(formData: FormData) {
    return this.garantie.uploadPhotoCaution(formData);
  }
  uploadImageGarantie(formData: FormData) {
    return this.garantie.uploadImageGarantie(formData);
  }
  uploadDocumentGarantie(formData: FormData) {
    return this.garantie.uploadDocumentGarantie(formData);
  }
  saveGarantieProposee(data: Record<string, unknown>) {
    return this.garantie.saveGarantieProposee(data);
  }
  choixGarantie(data: { crActifGarantie: number; garantie: 1 | 2 }) {
    return this.garantie.choixGarantie(data);
  }
  uploadDocumentAnalyse(formData: FormData) {
    return this.garantie.uploadDocumentAnalyse(formData);
  }
  deleteDocumentAnalyse(id: number) {
    return this.garantie.deleteDocumentAnalyse(id);
  }
  saveDocGarantieAuth(vehiculeId: number, libelle: string, file: File) {
    return this.garantie.saveDocGarantieAuth(vehiculeId, libelle, file);
  }
  authentificationGage(authGages: { id: number; auth_gage: 0 | 1 }[]) {
    return this.garantie.authentificationGage(authGages);
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // WORKFLOW
  // ═══════════════════════════════════════════════════════════════════════════
  saveCrdObservation(data: CreditActionPayload, creditSpecial?: number) {
    return this.workflow.saveCrdObservation(data, creditSpecial);
  }
  pauseDemande(data: CreditActionPayload) {
    return this.workflow.pauseDemande(data);
  }
  playDemande(data: CreditActionPayload) {
    return this.workflow.playDemande(data);
  }
  rappelDossierValide(data: CreditActionPayload) {
    return this.workflow.rappelDossierValide(data);
  }
  remettreEnCircuit(data: {
    refDemande: string;
    observation: string;
    motif: string;
    decision: number;
    password: string;
  }) {
    return this.workflow.remettreEnCircuit(data);
  }
  clotureDemande(data: CreditActionPayload) {
    return this.workflow.clotureDemande(data);
  }
  checkActeOrVisite(data: CreditActionPayload) {
    return this.workflow.checkActeOrVisite(data);
  }
  searchTirage(numDmde: string) {
    return this.workflow.searchTirage(numDmde);
  }
  saveTirage(data: CreditSaveTirage) {
    return this.workflow.saveTirage(data);
  }
  saveComiteDecision(data: CreditSaveComite) {
    return this.workflow.saveComiteDecision(data);
  }
  savePreComiteDecision(data: CreditSaveComite) {
    return this.workflow.savePreComiteDecision(data);
  }
  saveDecisionFinale(data: CreditSaveDecisionFinale) {
    return this.workflow.saveDecisionFinale(data);
  }
  submitAnalyse(data: { refDemande: string; observation: string; password: string }) {
    return this.workflow.submitAnalyse(data);
  }
  savePropositionAR(data: Record<string, unknown>) {
    return this.workflow.savePropositionAR(data);
  }
  saveContreEvaluation(data: Record<string, unknown>) {
    return this.workflow.saveContreEvaluation(data);
  }
  calculateFraisDossier(data: Record<string, unknown>) {
    return this.workflow.calculateFraisDossier(data);
  }
  saveSWOT(data: Record<string, unknown>) {
    return this.workflow.saveSWOT(data);
  }
  annulerDecaissement(data: CreditActionPayload) {
    return this.workflow.annulerDecaissement(data);
  }
  workByDerogation(data: CreditActionPayload) {
    return this.workflow.workByDerogation(data);
  }
  saveAvsf(data: Record<string, unknown>) {
    return this.workflow.saveAvsf(data);
  }
  modifAvsf(id: number, data: Record<string, unknown>) {
    return this.workflow.modifAvsf(id, data);
  }
  saveDecaissement(data: { refDemande: string; numPret: string; numContrat: string }) {
    return this.workflow.saveDecaissement(data);
  }
  updateMontantEmprunte(data: Record<string, unknown>) {
    return this.workflow.updateMontantEmprunte(data);
  }
  envoiAvanceFond(data: CreditActionPayload) {
    return this.workflow.envoiAvanceFond(data);
  }
  ajournAvsf(data: CreditActionPayload) {
    return this.workflow.ajournAvsf(data);
  }
  ajouterNumPerfectAvsf(id: number, data: Record<string, unknown>) {
    return this.workflow.ajouterNumPerfectAvsf(id, data);
  }
  affecterDemandeAR(
    data: {
      refDemande: string;
      decision: number;
      zone: number;
      codeAr: string;
      password: string;
      observation?: string;
    },
    creditSpecial?: number,
  ) {
    return this.workflow.affecterDemandeAR(data, creditSpecial);
  }
  deplacerUnDossier(data: { refDemande: string; statut: number; observation: string }) {
    return this.workflow.deplacerUnDossier(data);
  }

  affecterSuperviseurPME(
    data: {
      refDemande: string;
      decision: number;
      supOpSen: number;
      password: string;
      observation?: string;
    },
    creditSpecial?: number,
  ) {
    return this.workflow.affecterSuperviseurPME(data, creditSpecial);
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // EMPLOYEURS
  // ═══════════════════════════════════════════════════════════════════════════
  getListeEmployeurs(action?: 'valide' | 'rejette') {
    return this.employeur.getListeEmployeurs(action);
  }
  getDetailEmployeur(id: number) {
    return this.employeur.getDetailEmployeur(id);
  }
  getObservationsEmployeur(id: number) {
    return this.employeur.getObservationsEmployeur(id);
  }
  getDocumentsEmployeur(id: number) {
    return this.employeur.getDocumentsEmployeur(id);
  }
  getSecteurActivites() {
    return this.employeur.getSecteurActivites();
  }
  getBanques() {
    return this.employeur.getBanques();
  }
  // Provide both names (saveEmployeur and createEmployeur) to keep compatibility with callers
  saveEmployeur(data: unknown) {
    return this.employeur.saveEmployeur(data);
  }
  createEmployeur(data: unknown) {
    return this.employeur.saveEmployeur(data);
  }
  updateEmployeur(data: unknown) {
    return this.employeur.updateEmployeur(data);
  }
  updateNumeroPerfectEmployeur(data: { employeur: number; numPerfect: string }) {
    return this.employeur.updateNumeroPerfect(data);
  }
  actionEmployeur(data: unknown) {
    return this.employeur.actionEmployeur(data);
  }
  uploadDocumentEmployeur(employeurId: number, libelle: string, description: string, file: File) {
    return this.employeur.uploadDocument(employeurId, libelle, description, file);
  }
  deleteDocumentEmployeur(id: number | string) {
    return this.employeur.deleteDocument(id);
  }

  getDemandesEmployeur(id: number) {
    return this.employeur.getDemandesEmployeur(id);
  }

  saveReferenceCommerciale(data: Record<string, unknown>) {
    return this.analyse.saveReferenceCommerciale(data);
  }

  getReferenceCommercialeListe(refDemande: string) {
    return this.analyse.getReferenceCommercialeListe(refDemande);
  }

  saveReferenceFamiliale(data: Record<string, unknown>) {
    return this.analyse.saveReferenceFamiliale(data);
  }

  getReferenceFamilialeListe(refDemande: string) {
    return this.analyse.getReferenceFamilialeListe(refDemande);
  }

  saveVenteSaisonnalite(data: Record<string, unknown>) {
    return this.analyse.saveVenteSaisonnalite(data);
  }

  getVenteSaisonnaliteList(refDemande: string) {
    return this.analyse.getVenteSaisonnaliteList(refDemande);
  }

  saveFicheProd(data: Record<string, unknown>) {
    return this.analyse.saveFicheProd(data);
  }

  getFicheProdListe(refDemande: string) {
    return this.analyse.getFicheProdListe(refDemande);
  }

  saveCrFicheProdMp(data: Record<string, unknown>) {
    return this.analyse.saveCrFicheProdMp(data);
  }

  getListeFicheProdMp(ficheProdId: number) {
    return this.analyse.getListeFicheProdMp(ficheProdId);
  }

  saveRecapVente(data: Record<string, unknown>) {
    return this.analyse.saveRecapVente(data);
  }

  getRecapVenteListe(refDemande: string) {
    return this.analyse.getRecapVenteListe(refDemande);
  }
}
